import {
  createSingleFilter,
  createMultipleFilter,
  type FilterElementInput,
} from "app/utils/filters";

/**
 * Builds an array of provider filters from a list of provider values
 *
 * @param providers - Array of provider values (e.g., ["aws", "azure"])
 * @param fieldName - The field name to filter on (e.g., "provider", "qualified-name")
 * @param operator - The filter operator (e.g., "equals", "contains")
 * @returns Array of FilterElementInput objects, one per provider
 */
export function buildProviderFilters(
  providers: string[] | null | undefined,
  fieldName: string,
  operator: string,
): FilterElementInput[] {
  if (!providers || providers.length === 0) {
    return [];
  }

  return providers
    .map((provider) => createSingleFilter(fieldName, provider, operator))
    .filter((filter): filter is FilterElementInput => filter !== undefined);
}

/**
 * Combines a search filter with provider filters using AND/OR logic
 *
 * Logic:
 * - If no provider filters: return search filter as-is
 * - If no search filter: return combined provider filters with OR
 * - If both: return (searchFilter AND (provider1 OR provider2 OR ...))
 *
 * @param searchFilter - The search/query filter
 * @param providerFilters - Array of provider filters to OR together
 * @returns Combined FilterElementInput or null if no filters
 */
export function combineFilters(
  searchFilter: FilterElementInput | null,
  providerFilters: FilterElementInput[],
): FilterElementInput | null {
  if (providerFilters.length === 0) {
    return searchFilter;
  }

  // Build OR filter for multiple providers (e.g., AWS OR Azure)
  const providerFilter = createMultipleFilter(providerFilters, "OR");

  // If no search filter, return just the provider filter
  if (!searchFilter) {
    return providerFilter ?? null;
  }

  // Combine search filter AND provider filter
  // e.g., (name contains "prod") AND (provider = AWS OR provider = Azure)
  return createMultipleFilter([searchFilter, providerFilter], "AND") ?? null;
}
